import React, { useRef, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import DataGrid from "../../components/DataGrid/index.js";
import QueryLentaModal from "./QueryLentaModal";

export default function QueriesLentasList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const gridRef = useRef();

  const filtroInicial = searchParams.get("filtro") || "";
  const [periodoFiltro, setPeriodoFiltro] = useState(searchParams.get("periodo") || "hoje");
  const [dataInicio, setDataInicio] = useState(searchParams.get("dataInicio") || "");
  const [dataFim, setDataFim] = useState(searchParams.get("dataFim") || "");
  const [commandFiltro, setCommandFiltro] = useState(searchParams.get("command") || "");
  const [tempoMinimo, setTempoMinimo] = useState(searchParams.get("tempoMinimo") || "");
  
  const [modalAberto, setModalAberto] = useState(false);
  const [queryIdSelecionada, setQueryIdSelecionada] = useState(null);

  const handlePeriodoChange = (valor) => {
    setPeriodoFiltro(valor);
    const params = new URLSearchParams(searchParams);
    if (valor !== "") {
      params.set("periodo", valor);
    } else {
      params.delete("periodo");
    }
    if (valor !== "entre_datas") {
      params.delete("dataInicio");
      params.delete("dataFim");
      setDataInicio("");
      setDataFim("");
    }
    setSearchParams(params);
  };

  const handleDataChange = (campo, valor) => {
    const setter = campo === "dataInicio" ? setDataInicio : setDataFim;
    setter(valor);
    const params = new URLSearchParams(searchParams);
    if (valor !== "") {
      params.set(campo, valor);
    } else {
      params.delete(campo);
    }
    setSearchParams(params);
  };

  const handleCommandChange = (valor) => {
    setCommandFiltro(valor);
    const params = new URLSearchParams(searchParams);
    if (valor !== "") {
      params.set("command", valor);
    } else {
      params.delete("command");
    }
    setSearchParams(params);
  };

  const handleTempoMinimoChange = (valor) => {
    setTempoMinimo(valor);
    const params = new URLSearchParams(searchParams);
    if (valor !== "") {
      params.set("tempoMinimo", valor);
    } else {
      params.delete("tempoMinimo");
    }
    setSearchParams(params);
  };

  const abrirModal = (id) => {
    setQueryIdSelecionada(id);
    setModalAberto(true);
  };

  const formatarData = (data) => {
    if (!data) return "-";
    return new Date(data).toLocaleString("pt-BR");
  };

  const getColumns = () => [
    {
      key: "dataCaptura",
      header: "Data",
      width: "160px",
      render: (row) => formatarData(row.dataCaptura),
    },
    {
      field: "loginName",
      header: "Login",
      width: "150px",
    },
    {
      field: "command",
      header: "Comando",
      width: "100px",
    },
    {
      key: "tempo",
      header: "Tempo",
      width: "100px",
      className: "text-right",
      headerClassName: "text-right",
      render: (row) => (
        <span className={row.totalElapsedSeconds > 60 ? "text-red-600 font-medium" : ""}>
          {row.tempoFormatado}
        </span>
      ),
    },
    {
      key: "bloqueio",
      header: "Bloqueio",
      width: "90px",
      className: "text-center",
      headerClassName: "text-center",
      render: (row) => row.temBloqueio ? (
        <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">Sim</span>
      ) : (
        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-500">Não</span>
      ),
    },
    {
      key: "queryResumo",
      header: "Query",
      render: (row) => (
        <span className="text-gray-600 text-sm font-mono truncate block max-w-md">
          {row.queryTextResumo || "-"}
        </span>
      ),
    },
    {
      key: "acoes",
      header: "Ações",
      width: "100px",
      className: "text-center",
      headerClassName: "text-center",
      render: (row) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            abrirModal(row.id);
          }}
          className="px-3 py-1.5 text-sm rounded bg-slate-800 text-white hover:bg-slate-700"
        >
          Detalhar
        </button>
      ),
    },
  ];

  const externalFilters = useMemo(() => {
    const filters = {};
    if (periodoFiltro !== "") filters.periodo = periodoFiltro;
    if (dataInicio !== "") filters.dataInicio = dataInicio;
    if (dataFim !== "") filters.dataFim = dataFim;
    if (commandFiltro !== "") filters.command = commandFiltro;
    if (tempoMinimo !== "") filters.tempoMinimo = tempoMinimo;
    return filters;
  }, [periodoFiltro, dataInicio, dataFim, commandFiltro, tempoMinimo]);

  const limparFiltros = () => {
    setPeriodoFiltro("hoje");
    setDataInicio("");
    setDataFim("");
    setCommandFiltro("");
    setTempoMinimo("");
    setSearchParams({ periodo: "hoje" });
  };

  const temFiltrosAtivos = periodoFiltro !== "hoje" || dataInicio || dataFim || commandFiltro || tempoMinimo;

  return (
    <div>
      <div className="flex flex-col gap-4 mb-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold">Queries Lentas</h1>
            <p className="text-sm text-gray-500">Monitoramento de queries com tempo elevado de execução</p>
            {temFiltrosAtivos && (
              <button onClick={limparFiltros} className="text-sm text-blue-600 hover:underline">
                Limpar filtros
              </button>
            )}
          </div>
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-lg shadow">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Período:</span>
            <select
              value={periodoFiltro}
              onChange={(e) => handlePeriodoChange(e.target.value)}
              className="border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
            >
              <option value="">Todos</option>
              <option value="ultima_hora">Última hora</option>
              <option value="hoje">Hoje</option>
              <option value="entre_datas">Entre datas</option>
            </select>
          </div>

          {periodoFiltro === "entre_datas" && (
            <>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">De:</span>
                <input
                  type="date"
                  value={dataInicio}
                  onChange={(e) => handleDataChange("dataInicio", e.target.value)}
                  className="border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Até:</span>
                <input
                  type="date"
                  value={dataFim}
                  onChange={(e) => handleDataChange("dataFim", e.target.value)}
                  className="border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                />
              </div>
            </>
          )}

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Comando:</span>
            <select
              value={commandFiltro}
              onChange={(e) => handleCommandChange(e.target.value)}
              className="border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
            >
              <option value="">Todos</option>
              <option value="SELECT">SELECT</option>
              <option value="UPDATE">UPDATE</option>
              <option value="INSERT">INSERT</option>
              <option value="DELETE">DELETE</option>
              <option value="EXECUTE">EXECUTE</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Tempo mínimo:</span>
            <select
              value={tempoMinimo}
              onChange={(e) => handleTempoMinimoChange(e.target.value)}
              className="border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
            >
              <option value="">Qualquer</option>
              <option value="5">5+ segundos</option>
              <option value="10">10+ segundos</option>
              <option value="30">30+ segundos</option>
              <option value="60">1+ minuto</option>
              <option value="300">5+ minutos</option>
            </select>
          </div>
        </div>
      </div>

      <DataGrid
        ref={gridRef}
        endpoint={`${process.env.REACT_APP_API}/monitoramento/queries-lentas`}
        columns={getColumns()}
        filters={externalFilters}
        initialFilter={filtroInicial}
        emptyMessage="Nenhuma query lenta encontrada"
      />

      {/* Modal */}
      <QueryLentaModal
        isOpen={modalAberto}
        onClose={() => setModalAberto(false)}
        queryId={queryIdSelecionada}
      />
    </div>
  );
}